// crewbit-admin/src/components/money/MarkPaidModal.tsx
//
// Centered confirm modal for `set_invoice_paid_manually`. Amount (default
// = total), method, date, notes. Money values pass through MoneyInput per
// Hard Rule #34.

import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney, parseMoney, isPositive } from '../../lib/money';
import { markPaidManually } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  defaultAmount: number | string;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

const METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MarkPaidModal({ isOpen, invoiceId, defaultAmount, onClose, onResult }: Props) {
  const [amount, setAmount] = useState(canonicalMoney(defaultAmount));
  const [method, setMethod] = useState('cash');
  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const valid = isPositive(parseMoney(amount));

  const onConfirm = async () => {
    if (!valid) return;
    setLoading(true);
    const result = await markPaidManually(invoiceId, {
      amount: Number(parseMoney(amount)),
      method,
      receivedAt: new Date(date).toISOString(),
      notes: notes.trim() || null,
    });
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.already_paid ? 'Invoice already paid.' : 'Payment recorded.');
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to record payment.');
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 460, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.greenWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <CheckCircle size={22} color={palette.green} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Record payment
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Logs a payment against this invoice in the ledger.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <Field label="Amount">
            <MoneyInput value={amount} onChange={setAmount} ariaLabel="Payment amount" />
          </Field>

          <Field label="Method">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              style={inputStyle}
            >
              {METHOD_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Date received">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Reference number, memo, etc."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.green,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Recording…' : 'Record payment'}</button>
        </div>
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}
      </div>
      {children}
    </label>
  );
}
